import React from "react";
import { Document, Page, Text, View, Image, StyleSheet } from "@react-pdf/renderer";
import { mergeCouponContent } from "./couponContentDefaults";

const COUPON_W = 1050;
const COUPON_H = 480;
const PAGE_PAD = 16;
const PAGE_INNER_W = 595.28 - PAGE_PAD * 2;
const SCALE = PAGE_INNER_W / COUPON_W;
const CARD_H = COUPON_H * SCALE;
const pt = (value) => Math.round(value * SCALE * 10) / 10;

const styles = StyleSheet.create({
  page: {
    padding: PAGE_PAD,
    backgroundColor: "#ffffff",
    fontFamily: "Helvetica",
  },
  card: {
    flexDirection: "row",
    width: "100%",
    height: CARD_H,
    backgroundColor: "#ffffff",
    overflow: "hidden",
  },
  leftStub: {
    width: "28%",
    backgroundColor: "#ffffff",
    borderRightWidth: 2,
    borderRightStyle: "dashed",
    borderRightColor: "#888888",
    paddingTop: pt(18),
    paddingBottom: pt(14),
    paddingHorizontal: pt(16),
    flexDirection: "column",
  },
  leftHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: pt(26),
  },
  logo: {
    width: pt(75),
    objectFit: "contain",
  },
  slBadgeDark: {
    borderWidth: 2,
    borderColor: "#222222",
    borderRadius: pt(10),
    paddingVertical: pt(5),
    paddingHorizontal: pt(10),
  },
  slBadgeDarkText: {
    fontSize: pt(12),
    fontFamily: "Helvetica-Bold",
    color: "#222222",
  },
  fieldsContainer: {
    flexDirection: "column",
  },
  fieldGroup: {
    marginBottom: pt(20),
  },
  fieldLabel: {
    fontSize: pt(14),
    fontFamily: "Helvetica-Bold",
    color: "#111111",
    marginBottom: pt(5),
  },
  fieldLine: {
    borderBottomWidth: 2,
    borderBottomColor: "#333333",
    minHeight: pt(28),
    paddingBottom: pt(2),
    justifyContent: "flex-end",
  },
  fieldValue: {
    fontSize: pt(14),
    color: "#111111",
  },
  leftFooter: {
    marginTop: pt(20),
    fontSize: pt(10),
    color: "#333333",
    textAlign: "center",
    lineHeight: 1.35,
  },
  rightPanel: {
    width: "72%",
    height: CARD_H,
    position: "relative",
    flexDirection: "column",
    overflow: "hidden",
  },
  raffleBg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "65%",
    objectFit: "cover",
  },
  rightContent: {
    height: CARD_H,
    paddingTop: pt(14),
    paddingHorizontal: pt(16),
    flexDirection: "column",
  },
  rightHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: pt(8),
  },
  logoLight: {
    width: pt(80),
    objectFit: "contain",
  },
  slBadgeLight: {
    borderWidth: 2,
    borderColor: "#ffffff",
    borderRadius: pt(10),
    paddingVertical: pt(5),
    paddingHorizontal: pt(12),
    backgroundColor: "#4d4d4d",
  },
  slBadgeLightText: {
    fontSize: pt(12),
    fontFamily: "Helvetica-Bold",
    color: "#ffffff",
  },
  footerSpacer: {
    flexGrow: 1,
  },
  footerBlock: {
    marginLeft: pt(-16),
    marginRight: pt(-16),
  },
  footerSection: {
    backgroundColor: "#c41e26",
    borderTopWidth: 3,
    borderTopColor: "#ffd700",
    paddingVertical: pt(16),
    paddingHorizontal: pt(18),
    flexDirection: "row",
    alignItems: "center",
  },
  footerCol: {
    width: "32%",
    paddingRight: pt(9),
  },
  footerCenter: {
    width: "22%",
    alignItems: "center",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: "#c9a800",
    paddingHorizontal: pt(9),
    justifyContent: "center",
  },
  footerTermsWrap: {
    width: "46%",
    paddingLeft: pt(9),
  },
  footerPromo: {
    fontSize: pt(12),
    fontFamily: "Helvetica-Bold",
    color: "#ffffff",
    lineHeight: 1.5,
  },
  brandTitle: {
    fontSize: pt(14),
    fontFamily: "Helvetica-Bold",
    color: "#ffd700",
    lineHeight: 1.2,
    textAlign: "center",
  },
  brandSubtitle: {
    fontSize: pt(10),
    fontFamily: "Helvetica-Bold",
    color: "#ffffff",
    marginTop: pt(2),
    textAlign: "center",
  },
  termsBox: {
    backgroundColor: "#2a0808",
    borderWidth: 1,
    borderColor: "#c9a800",
    borderRadius: pt(8),
    paddingVertical: pt(10),
    paddingHorizontal: pt(12),
  },
  drawBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#5c1010",
    borderWidth: 1,
    borderColor: "#c9a800",
    borderRadius: pt(20),
    paddingVertical: pt(4),
    paddingHorizontal: pt(12),
    marginBottom: pt(8),
  },
  drawBadgeText: {
    fontSize: pt(10),
    fontFamily: "Helvetica-Bold",
    color: "#ffd700",
  },
  termsTitle: {
    fontSize: pt(10),
    fontFamily: "Helvetica-Bold",
    color: "#ffd700",
    marginBottom: pt(5),
  },
  termsItem: {
    fontSize: pt(10),
    color: "#f5f5f5",
    lineHeight: 1.5,
    marginBottom: pt(2),
  },
  contactBar: {
    backgroundColor: "#0a0a0a",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: pt(8),
    paddingHorizontal: pt(16),
    borderTopWidth: 1,
    borderTopColor: "#3a3000",
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginHorizontal: pt(10),
  },
  contactLabel: {
    fontSize: pt(9),
    fontFamily: "Helvetica-Bold",
    color: "#ffd700",
    marginRight: pt(6),
  },
  contactText: {
    fontSize: pt(10),
    color: "#e8e8e8",
  },
  sideNote: {
    position: "absolute",
    right: pt(4),
    top: CARD_H * 0.5,
    fontSize: pt(8),
    color: "#999999",
    transform: "rotate(90deg)",
  },
});

const Field = ({ label, value, wrap = false }) => (
  <View style={styles.fieldGroup}>
    <Text style={styles.fieldLabel}>{label}</Text>
    <View style={styles.fieldLine}>
      <Text style={styles.fieldValue} wrap={wrap}>{value || " "}</Text>
    </View>
  </View>
);

const BuyWinCouponPdf = ({ couponData = {} }) => {
  const {
    order_no = "N/A",
    name = "",
    cpr = "N/A",
    mob = "",
    raffleCoupon = "",
    drawDateFormatted = "N/A",
    promotionFrom = "",
    promotionTo = "",
    raffleBg = "",
    logoSrc = "",
    content = {},
  } = couponData;

  const couponContent = mergeCouponContent(content);
  const logoImage = logoSrc || null;
  const termsItems = couponContent.terms_items || [];
  const showPromotionDates =
    couponContent.show_promotion_dates === "Y" && promotionFrom && promotionTo;

  return (
    <Document>
      <Page size="A4" orientation="portrait" style={styles.page}>
        <View style={styles.card}>
          <View style={styles.leftStub}>
            <View style={styles.leftHeader}>
              {logoImage ? <Image src={logoImage} style={styles.logo} /> : null}
              <View style={styles.slBadgeDark}>
                <Text style={styles.slBadgeDarkText}>SL.NO : {order_no}</Text>
              </View>
            </View>

            <View style={styles.fieldsContainer}>
              <Field label="Name :" value={name} />
              <Field label="C.P.R :" value={cpr} />
              <Field label="Mob :" value={mob} />
              <Field label="Raffle Coupon :" value={raffleCoupon} />
              <Field label="Draw Date :" value={drawDateFormatted} wrap />
            </View>

            <Text style={styles.leftFooter} wrap>
              {couponContent.promo_text}
            </Text>
          </View>

          <View style={styles.rightPanel}>
            {raffleBg ? <Image src={raffleBg} style={styles.raffleBg} /> : null}

            <View style={styles.rightContent}>
              <View style={styles.rightHeader}>
                {logoImage ? <Image src={logoImage} style={styles.logoLight} /> : null}
                <View style={styles.slBadgeLight}>
                  <Text style={styles.slBadgeLightText}>SL.NO : {order_no}</Text>
                </View>
              </View>

              <View style={styles.footerSpacer} />

              <View style={styles.footerBlock}>
                <View style={styles.footerSection}>
                  <View style={styles.footerCol}>
                    <Text style={styles.footerPromo} wrap>
                      {couponContent.promo_text}
                    </Text>
                  </View>

                  <View style={styles.footerCenter}>
                    <Text style={styles.brandTitle}>{couponContent.brand_title}</Text>
                    <Text style={styles.brandSubtitle}>{couponContent.brand_subtitle}</Text>
                  </View>

                  <View style={styles.footerTermsWrap}>
                    <View style={styles.termsBox}>
                      {drawDateFormatted !== "N/A" ? (
                        <View style={styles.drawBadge}>
                          <Text style={styles.drawBadgeText}>DRAW: {drawDateFormatted}</Text>
                        </View>
                      ) : null}
                      <Text style={styles.termsTitle}>{couponContent.terms_title}</Text>
                      {termsItems.map((term, index) => (
                        <Text key={index} style={styles.termsItem} wrap>
                          {"\u2022"} {term}
                        </Text>
                      ))}
                      {showPromotionDates ? (
                        <Text style={styles.termsItem} wrap>
                          {"\u2022"} {couponContent.promotion_label}: {promotionFrom} to {promotionTo}
                        </Text>
                      ) : null}
                    </View>
                  </View>
                </View>

                <View style={styles.contactBar}>
                  <View style={styles.contactItem}>
                    <Text style={styles.contactLabel}>MOB</Text>
                    <Text style={styles.contactText}>{couponContent.contact_mobile}</Text>
                  </View>
                  <View style={styles.contactItem}>
                    <Text style={styles.contactLabel}>LOCATION</Text>
                    <Text style={styles.contactText} wrap>
                      {couponContent.contact_location}
                    </Text>
                  </View>
                </View>
              </View>
            </View>

            <Text style={styles.sideNote}>{couponContent.side_note}</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default BuyWinCouponPdf;
